/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/consistent-indexed-object-style */
/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { ApiSysPermissionService } from '@services/api/api-sys-permissions.service';
import { ApiSysService } from '@services/api/api-sys.service';
import { AuthService } from '@services/auth/auth.service';
import { LanguageService } from '@services/language/language.service';
import { LocalStorageService } from '@services/localStorage/local-storage-service.service';
import { PermissionSideBarService } from '@services/permissions/permission-side-bar.service';
import { parseUserTypeId, UserTypeId } from '@shared/models/enums';
import { filter, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastService } from '@core/services/toast/toast.service';
@Component({
  selector: 'app-sidebar',
  imports: [RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent implements OnInit, OnDestroy {
  isSidebarOpen = false;
  activeIcon = 'general';
  activeSection = 'manager';
  oldActiveIcon = '';
  activeSubSection = '';
  expandedSections: { [key: string]: boolean } = {};

  userType: UserTypeId | null = null;
  strCode: any;
  hasInfrastructures = false;
  userId: string;

  private sub: Subscription | undefined;
  private destroy$ = new Subject<void>();
  private hoverTimeout: ReturnType<typeof setTimeout> | null = null;

  translateData: any = {};

  readonly TOTAL_GRID_ITEMS = 5;
  menuItems: any[] = [];
  sectionItems: any = {};

  public petitionsCount: number = 0;

  @HostListener('window:petitionsCountChanged')
  onPetitionsCountChanged(): void {
    this.loadPetitionsCount();
  }

  constructor(
    private langService: LanguageService,
    private auth: AuthService,
    private apiSys: ApiSysService,
    private router: Router,
    private ls: LocalStorageService,
    private route: ActivatedRoute,
    private apiSysPerm: ApiSysPermissionService,
    public perSideBar: PermissionSideBarService,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService
  ) {
    this.userId = this.auth.getUserId();
  }

  private loadPetitionsCount(): void {
    this.apiSys
      .countAllUserPetitions(this.auth.getToken())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.petitionsCount = response.data;
        },
        error: (error) => {
          console.error('Error fetching petitions count:', error);
          this.petitionsCount = 0;
        }
      });
  }

  private shouldLoadPetitionsCount(userType: UserTypeId | null | undefined): boolean {
    return userType !== UserTypeId.USER;
  }

  private translates(): void {
    this.langService
      .getSharedDictionary('sidebar')
      .pipe(takeUntil(this.destroy$))
      .subscribe((translation) => {
        this.translateData = translation;

        this.menuItems = [
          {
            id: 'general',
            icon: 'assets/icons/sidebar/general.svg',
            label: this.translateData['general'],
            requiresPermission: false,
            visible: true
          },
          {
            id: 'document',
            icon: 'assets/icons/sidebar/document.svg',
            label: this.translateData['document'],
            requiresPermission: true,
            permissionKey: 'Document',
            requiresInfrastructure: true,
            visible: true
          },
          {
            id: 'monitor',
            icon: 'assets/icons/sidebar/monitor.svg',
            label: this.translateData['monitor'],
            requiresPermission: true,
            permissionKey: 'Monitor',
            requiresInfrastructure: true,
            visible: true
          },
          {
            id: 'uas',
            icon: 'assets/icons/sidebar/uas.svg',
            label: this.translateData['uas'],
            requiresPermission: true,
            permissionKey: 'UAS',
            visible: true
          },
          {
            id: 'resources',
            icon: 'assets/icons/sidebar/resources.svg',
            label: this.translateData['resources'],
            requiresPermission: false,
            permissionKey: 'Resources',
            visible: true
          }
        ];

        this.sectionItems = {
          general: [
            {
              id: 'manager',
              label: this.translateData['manager'] || 'Manager',
              route: '/landing'
            }
          ],
          document: [
            {
              id: 'document',
              label: this.translateData['overview'] || 'Overview',
              route: ['/document', 'strCode'],
              requiresInfrastructure: true
            },
            {
              id: 'general-information',
              label: this.translateData['general_info'] || 'General information',
              route: ['/document', 'strCode', 'general-information'],
              requiresInfrastructure: true,
              permissionModule: 'Document',
              permissionSection: 'GRLINF',
              hasSubsections: true,
              subsections: [
                {
                  id: 'administrative-information',
                  label: this.translateData['administrative_info'] || 'Administrative information',
                  route: ['/document', 'strCode', 'general-information', 'administrative-information']
                },
                {
                  id: 'technical-information',
                  label: this.translateData['technical_info'] || 'Technical information',
                  route: ['/document', 'strCode', 'general-information', 'technical-information']
                }
              ]
            },
            {
              id: 'aeronautical-information',
              label: this.translateData['aeronautical_info'] || 'Aeronautical information',
              route: ['/document', 'strCode', 'aeronautical-information'],
              requiresInfrastructure: true,
              permissionModule: 'Document',
              permissionSection: 'AERINF',
              hasSubsections: true,
              subsections: [
                {
                  id: 'aeronautical-documents',
                  label: this.translateData['aeronautical_doc'] || 'Aeronautical documents',
                  route: ['/document', 'strCode', 'aeronautical-information', 'aeronautical-documents']
                },
                {
                  id: 'aeronautical-operations',
                  label: this.translateData['operations'] || 'Operations',
                  route: ['/document', 'strCode', 'operations-list']
                }
              ]
            },
            {
              id: 'manual',
              label: this.translateData['manual'] || 'Manual',
              route: ['/document', 'strCode', 'manual'],
              requiresInfrastructure: true,
              permissionModule: 'Document',
              permissionSection: 'MAN',
              hasSubsections: true,
              subsections: [
                {
                  id: 'operating-manual',
                  label: this.translateData['operating_manual'] || 'Operating manual',
                  route: ['/document', 'strCode', 'manual', 'operating-manual']
                },
                {
                  id: 'operating-protocols',
                  label: this.translateData['operating_protocols'] || 'Operating protocols',
                  route: ['/document', 'strCode', 'manual', 'operating-protocols']
                }
              ]
            },
            {
              id: 'supervision',
              label: this.translateData['supervision'] || 'Supervision',
              route: ['/document', 'strCode', 'supervision'],
              requiresInfrastructure: true,
              permissionModule: 'Document',
              permissionSection: 'SUP',
              hasSubsections: true,
              subsections: [
                {
                  id: 'supervision-documents',
                  label: this.translateData['supervision_doc'] || 'Supervision documents',
                  route: ['/document', 'strCode', 'supervision', 'supervision-documents']
                },
                {
                  id: 'supervision-inspections',
                  label: this.translateData['inspections'] || 'Inspections',
                  route: ['/document', 'strCode', 'supervision', 'supervision-inspections']
                }
              ]
            },
            {
              id: 'training',
              label: this.translateData['training'] || 'Training',
              route: ['/document', 'strCode', 'training'],
              requiresInfrastructure: true,
              permissionModule: 'Document',
              permissionSection: 'TRAIN',
              hasSubsections: true,
              subsections: [
                {
                  id: 'training-programme',
                  label: this.translateData['training_programme'] || 'Training programme',
                  route: ['/document', 'strCode', 'training', 'training-programme']
                },
                {
                  id: 'training-courses',
                  label: this.translateData['training_courses'] || 'Training courses',
                  route: ['/document', 'strCode', 'training', 'training-courses']
                }
              ]
            },
            {
              id: 'maintenance',
              label: this.translateData['maintenance'] || 'Maintenance',
              route: ['/document', 'strCode', 'maintenance'],
              requiresInfrastructure: true,
              permissionModule: 'Document',
              permissionSection: 'MNT',
              hasSubsections: true,
              subsections: [
                {
                  id: 'maintenance-documents',
                  label: this.translateData['maintenance_doc'] || 'Maintenance documents',
                  route: ['/document', 'strCode', 'maintenance', 'maintenance-documents']
                },
                {
                  id: 'maintenance-incidents',
                  label: this.translateData['incidents'] || 'Incidents',
                  route: ['/document', 'strCode', 'maintenance', 'maintenance-incidents']
                }
              ]
            }
          ],
          monitor: [
            {
              id: 'monitor',
              label: this.translateData['overview'] || 'Overview',
              route: ['/monitor', 'strCode'],
              requiresInfrastructure: true
            },
            {
              id: 'meteo',
              label: this.translateData['meteo'] || 'Meteorological information',
              route: ['/monitor', 'strCode', 'meteo'],
              requiresInfrastructure: true,
              permissionModule: 'Monitor',
              permissionSection: 'METEO'
            },
            {
              id: 'vision',
              label: this.translateData['vision'] || 'Vision',
              route: ['/monitor', 'strCode', 'vision'],
              requiresInfrastructure: true,
              permissionModule: 'Monitor',
              permissionSection: 'VISION'
            },
            {
              id: 'switch',
              label: this.translateData['switch'] || 'Switch',
              route: ['/monitor', 'strCode', 'equipment'],
              requiresInfrastructure: true,
              permissionModule: 'Monitor',
              permissionSection: 'SWITCH'
            },
            {
              id: 'tracking',
              label: this.translateData['tracking'] || 'Aircraft tracking system',
              route: ['/monitor', 'strCode', 'tracking'],
              requiresInfrastructure: true,
              permissionModule: 'Monitor',
              permissionSection: 'TRACKING'
            },
            {
              id: 'monitoring',
              label: this.translateData['monitoring'] || 'Monitoring',
              route: ['/monitor/monitoring'],
              requiresInfrastructure: false,
              permissionModule: 'Monitor',
              permissionSection: 'MONITORING'
            }
          ],
          uas: [
            {
              id: 'uas',
              label: this.translateData['overview'] || 'Overview',
              route: '/uas',
              permissionModule: 'UAS'
            }
          ],
          resources: [
            {
              id: 'resources',
              label: this.translateData['overview'] || 'Overview',
              route: '/resources'
            },
            {
              id: 'organization',
              label: this.translateData['organization'] || 'Organizations',
              route: '/resources/organization'
            },
            {
              id: 'personnel',
              label: this.translateData['personnel'] || 'Personnel',
              route: '/resources/personnel',
              conditionalRoute: {
                condition: 'userType3',
                route: ['/resources/personnel', 'userId']
              }
            },
            {
              id: 'infrastructure',
              label: this.translateData['infrastructure'] || 'Infrastructures',
              route: '/resources/infrastructure',
              hasSubsections: true,
              subsections: [
                {
                  id: 'network',
                  label: this.translateData['network'] || 'Network',
                  route: '/resources/network'
                },
                {
                  id: 'infrastructure-components',
                  label: this.translateData['infrastructure_components'] || 'Components',
                  route: '/resources/infrastructure/components'
                }
              ]
            },
            {
              id: 'aircraft',
              label: this.translateData['aircraft'] || 'Aircraft',
              route: '/resources/aircraft',
              hasSubsections: true,
              subsections: [
                {
                  id: 'fleet',
                  label: this.translateData['fleet'] || 'Fleet',
                  route: '/resources/fleet'
                }
              ]
            },
            {
              id: 'separator',
              type: 'separator'
            },
            {
              id: 'petitions',
              label: this.translateData['petitions'] || 'Petitions',
              route: '/petitions',
              disabledFor: 'userType3',
              showBadge: true
            },
            {
              id: 'issues',
              label: this.translateData['issues'] || 'Issues',
              route: '/issues',
              onlySuperAdmin: true,
              disabledFor: 'notSuperAdmin'
            }
          ]
        };
        this.cdr.markForCheck();
      });
  }

  get visibleMenuItems(): any[] {
    const visibleItems = this.menuItems.filter((item) => {
      return (
        !item.requiresPermission || (item.requiresPermission && this.perSideBar.canAccessModule(item.permissionKey!))
      );
    });

    const numberOfEmptyItems = Math.max(this.TOTAL_GRID_ITEMS - visibleItems.length, 0);
    const emptyItems = Array.from({ length: numberOfEmptyItems }, () => ({
      id: '',
      icon: '',
      label: '',
      visible: false
    }));

    return [...visibleItems, ...emptyItems];
  }

  handleSectionHover(item: any): void {
    if (item.requiresInfrastructure && !this.hasInfrastructures) {
      return;
    }

    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
    this.hoverTimeout = setTimeout(() => {
      if (item.requiresPermission) {
        if (this.perSideBar.canAccessModule(item.permissionKey!)) {
          this.activeSectionIcon(item.id);
        }
      } else {
        this.activeSectionIcon(item.id);
      }
    }, 100);
  }

  handleSectionMouseLeave(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  handleSectionClick(item: any, event: Event): void {
    event.preventDefault();

    if (item.requiresInfrastructure && !this.hasInfrastructures) {
      console.log("Error - There's no infrastructure");
      this.toastService.error(
        this.translateData?.error || 'Error',
        this.translateData?.access_without_infrastructure || 'Infrastructure must be created to access this feature'
      );
      return;
    }

    if (this.isMobile) {
      this.handleSectionHover(item);
      return;
    }
    if (item.requiresPermission && !this.perSideBar.canAccessModule(item.permissionKey!)) {
      return;
    }
    this.activeSectionIcon(item.id);

    const sectionItems = this.getSectionItems(item.id);
    const overviewItem = sectionItems.find((s: any) => s.id === item.id || s.id === 'manager');
    if (overviewItem) {
      this.handleItemAction(overviewItem);
    }
  }

  getIconHref(item: any): string | null {
    if (item.requiresPermission && !this.perSideBar.canAccessModule(item.permissionKey!)) {
      return null;
    }
    const sectionItems = this.getSectionItems(item.id);
    const overviewItem = sectionItems.find((s: any) => s.id === item.id || s.id === 'manager');
    if (!overviewItem) return null;
    const route = this.getRouteLink(overviewItem);
    if (!route) return null;
    const commands = Array.isArray(route) ? route : [route];
    return this.router.serializeUrl(this.router.createUrlTree(commands));
  }

  private get isMobile(): boolean {
    return window.innerWidth <= 480;
  }

  handleSubItemClick(event: Event, url: any, mod: any, section: any): void {
    event.stopPropagation();
    event.preventDefault();
    this.reload(url);
  }

  disabledSection(mod: any, section: any): boolean {
    if (this.perSideBar.canAccessSection(mod, section) === false) {
      return true;
    } else {
      return false;
    }
  }

  ngOnInit(): void {
    this.isSidebarOpen = false;
    this.expandedSections = {};
    this.translates();
    this.getDataRoute();

    // Cargar permisos primero
    this.apiSysPerm
      .getPermissionsMenu(this.auth.getToken())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const userType = parseUserTypeId(res.userType);
          this.userType = userType;
          this.perSideBar.setPermissions(userType, res.moduleAccess);
          if (this.shouldLoadPetitionsCount(userType)) {
            this.loadPetitionsCount();
          } else {
            this.petitionsCount = 0;
          }
        },
        error: (err) => {
          console.error('Error fetching permissions:', err);
          this.petitionsCount = 0;
        }
      });

    this.strCode = this.ls.getString('strId');

    // Fetch infrastructures: sets hasInfrastructures flag and, if no strCode is
    // stored in localStorage yet, picks the first infrastructure as the default.
    this.apiSys
      .fetchInfrastructures()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const infraList: any[] = res?.data ?? [];
          this.hasInfrastructures = infraList.length > 0;
          if (!this.strCode && infraList.length > 0 && infraList[0]?.str_id) {
            this.strCode = infraList[0].str_id;
            this.ls.setString('strId', this.strCode);
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error fetching infrastructures:', err);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
    this.sub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    document.body.classList.remove('no-scroll');
  }
  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  getDataRoute(): void {
    this.expandedSections = {};
    const deepestChild = this.getDeepestChild(this.route);
    this.activeIcon = deepestChild.snapshot.data['icon'];
    this.activeSection = deepestChild.snapshot.data['section'];

    this.updateExpandedSections(deepestChild);

    if (this.activeIcon === 'document' && this.activeSection === 'document') {
      this.activeSection = deepestChild.snapshot.data['section'];
    } else if (this.activeIcon === 'document') {
      this.activeSection = deepestChild.snapshot.params['docMod'] || deepestChild.snapshot.data['section'];
      this.expandedSections = { [this.activeSection]: true };
      this.activeSubSection = deepestChild.snapshot.params['docType'] || deepestChild.snapshot.data['subSection'];
    }

    this.oldActiveIcon = this.activeIcon;

    this.sub = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const child = this.getDeepestChild(this.route);

      this.expandedSections = {};
      this.activeIcon = child.snapshot.data['icon'];
      this.activeSection = child.snapshot.data['section'];

      // Evaluar condición específica para 'document'
      if (this.activeIcon === 'document' && this.activeSection === 'document') {
        this.activeSection = child.snapshot.data['section'];
      } else if (this.activeIcon === 'document') {
        this.activeSection = child.snapshot.params['docMod'] || child.snapshot.data['section'];
        this.expandedSections = { [this.activeSection]: true };
        this.activeSubSection = child.snapshot.params['docType'] || child.snapshot.data['subSection'];
      }
      this.updateExpandedSections(child);

      this.oldActiveIcon = this.activeIcon;
    });
  }

  toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
    document.body.classList.toggle('no-scroll', this.isSidebarOpen);
    this.cdr.markForCheck();
  }

  closeSidebar(): void {
    if (!this.isSidebarOpen) return;

    this.isSidebarOpen = false;
    document.body.classList.remove('no-scroll');
    this.expandedSections = { [this.activeSection]: true };
    this.activeIcon = this.oldActiveIcon;
    this.cdr.markForCheck();
  }

  onOverlayClick(): void {
    this.closeSidebar();
  }

  onSidebarClick(event: Event): void {
    event.stopPropagation();
  }

  // Escuchar la tecla Escape para cerrar el sidebar
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.isSidebarOpen) {
      event.preventDefault();
      this.closeSidebar();
    }
  }

  // Opcional: Cerrar sidebar al hacer clic fuera (alternativa al overlay)
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.isSidebarOpen) return;
    const target = event.target as HTMLElement;
    const sidebar = target.closest('.sidebar-container');
    const toggleBtn = target.closest('.toggle-btn');

    if (this.isSidebarOpen && !sidebar && !toggleBtn) {
      this.closeSidebar();
    }
  }

  // toggleSection(event: Event, section: string): void {
  //   event.stopPropagation();
  //   if (this.expandedSections[section]) {
  //     this.expandedSections = {};
  //   } else {
  //     this.expandedSections = { [section]: true };
  //   }
  // }

  toggleSection(event: Event, item: any): void {
    event.stopPropagation();
    event.preventDefault();

    this.expandedSections = {
      ...this.expandedSections,
      [item.id]: !this.expandedSections[item.id]
    };
    this.cdr.markForCheck();
  }

  activeSectionIcon(icon: string): void {
    this.activeIcon = icon;
    this.cdr.markForCheck();
  }

  reload(url: string): void {
    this.router.navigate([url]);
    this.closeSidebar();
  }

  async handleOrganizationClick(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    this.closeSidebar();
    if (this.auth.isSuperAdmin()) {
      this.router.navigate(['/resources/organization']);
    } else {
      const orgId = this.auth.getUser()?.org_id;

      if (orgId) {
        this.router.navigate(['/resources/organization', orgId]);
      }
    }
  }

  @HostListener('window:infrastructureChanged', ['$event'])
  onInfrastructureChanged(event: Event): void {
    const customEvent = event as CustomEvent;
    const newStrId = customEvent.detail.strId;
    if (newStrId && newStrId !== this.strCode) {
      this.strCode = newStrId;
      this.updateCurrentRoute();
    }
  }

  private updateCurrentRoute(): void {
    const currentUrl = this.router.url;
    const urlSegments = currentUrl.split('/').filter((s) => s);

    if (urlSegments[0] === 'document' || urlSegments[0] === 'monitor') {
      if (urlSegments.length > 1) {
        // Rutas compuestas donde el str_id está en posición 2, no en 1
        const compoundPrefix = `${urlSegments[0]}/${urlSegments[1]}`;
        const COMPOUND_ROUTES = ['monitor/monitoring'];
        if (COMPOUND_ROUTES.includes(compoundPrefix) && urlSegments.length > 2) {
          urlSegments[2] = this.strCode;
        } else {
          urlSegments[1] = this.strCode;
        }
        const newUrl = '/' + urlSegments.join('/');
        if (currentUrl !== newUrl) {
          this.router.navigate([newUrl], { replaceUrl: true });
        }
      }
    }
  }

  private updateExpandedSections(route: ActivatedRoute | null): void {
    if (!route) return;

    const url = this.router.url;
    const urlSegments = url.split('/').filter((s) => s);

    // Fleet
    if (urlSegments.includes('fleet') || urlSegments.includes('fleet')) {
      this.expandedSections['aircraft'] = true;
      this.activeSection = 'aircraft';
      this.activeSubSection = 'fleet';
    } else if (urlSegments.includes('aircraft') || urlSegments.includes('aircraft')) {
      this.expandedSections['aircraft'] = true;
      this.activeSection = 'aircraft';
      this.activeSubSection = '';
    }

    // Network
    if (urlSegments.includes('network') || urlSegments.includes('network')) {
      this.expandedSections['infrastructure'] = true;
      this.activeSection = 'infrastructure';
      this.activeSubSection = 'network';
    } else if (urlSegments.includes('infrastructure') || urlSegments.includes('infrastructure')) {
      this.expandedSections['infrastructure'] = true;
      this.activeSection = 'infrastructure';
      this.activeSubSection = '';
    }

    // Document
    if (this.activeIcon === 'document' && this.activeSection !== 'document') {
      this.expandedSections[this.activeSection] = true;
    }
  }

  logout(): void {
    this.auth.logout();
  }

  support(): void {
    this.toastService.warn(this.translateData?.warn || 'Warn', 'This feature is not yet implemented');
  }

  get isSuperAdmin(): boolean {
    return this.auth.isSuperAdmin();
  }

  getRouteLink(item: any): any[] | string | null {
    if (!item.route) return null;

    if (typeof item.route === 'string') {
      return item.route;
    }

    if (Array.isArray(item.route)) {
      // Verificar si requiere strCode y no está disponible
      if (item.requiresInfrastructure && !this.strCode) {
        console.warn('Cannot navigate: strCode is required but not available');
        return null;
      }

      return item.route.map((segment: string) => {
        if (segment === 'strCode') {
          if (!this.strCode) {
            console.error('strCode is required but not set');
            return 'null'; // O manejar de otra forma
          }
          return this.strCode;
        }
        if (segment === 'userId') return this.userId;
        return segment;
      });
    }

    return null;
  }

  shouldShowItem(item: any): boolean {
    if (item.onlySuperAdmin && !this.isSuperAdmin) {
      return false;
    }

    return true;
  }

  isItemDisabled(item: any): boolean {
    if (item.disabledFor === 'userType3' && this.userType === UserTypeId.USER) {
      return true;
    }

    if (item.disabledFor === 'notSuperAdmin' && !this.isSuperAdmin) {
      return true;
    }

    if (item.permissionModule && item.permissionSection) {
      return this.disabledSection(item.permissionModule, item.permissionSection);
    }

    return false;
  }

  handleItemAction(item: any, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Verificar si el item está deshabilitado
    if (this.isItemDisabled(item)) {
      console.warn('Item is disabled:', item.id);
      return;
    }

    // Manejar ruta condicional
    if (item.conditionalRoute && item.conditionalRoute.condition === 'userType3' && this.userType === UserTypeId.USER) {
      const route = this.getRouteLink({ route: item.conditionalRoute.route });
      if (route) {
        this.navigateToRoute(route, true);
      }
      return;
    }

    // Navegación normal
    const route = this.getRouteLink(item);
    if (route) {
      this.navigateToRoute(route);
    } else {
      console.error('Cannot generate route for item:', item);
    }
  }

  private navigateToRoute(route: any[] | string, shouldCloseSidebar: boolean = true): void {
    const routeArray = Array.isArray(route) ? route : [route];

    if (shouldCloseSidebar) {
      this.closeSidebar();
    }

    this.router
      .navigate(routeArray)
      .then((success) => {
        if (!success) {
          console.error('Navigation failed for route:', routeArray);
        }
      })
      .catch((error) => {
        console.error('Navigation error:', error);
      });
  }

  getSectionItems(sectionId: string): any[] {
    return this.sectionItems[sectionId] || [];
  }
}
